import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound, Loader2, ArrowLeft } from "lucide-react";
import { PrivacyConsent } from "../components/PrivacyConsent";
import { PasswordInput } from "../components/PasswordInput";

export default function Activate() {
  const { activate } = useAuth();
  const navigate = useNavigate();
  const [loginId, setLoginId] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [privacyOk, setPrivacyOk] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("La password deve avere almeno 8 caratteri"); return; }
    if (password !== confirm) { setError("Le password non coincidono"); return; }
    if (!privacyOk) { setError("Devi autorizzare il trattamento dei dati per attivare l'account"); return; }
    setLoading(true);
    try {
      await activate(loginId.trim(), code.trim(), password, privacyOk);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-brand rounded-xl p-3 text-white"><KeyRound className="w-7 h-7" /></div>
          <div>
            <h1 className="font-display text-3xl font-bold">Prima attivazione</h1>
            <p className="text-slate-600 text-base">Attiva il tuo accesso e scegli la password</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4 bg-white p-6 rounded-xl border-2">
          <p className="text-base text-slate-600">Inserisci l'<b>ID di accesso</b> e il <b>codice di attivazione</b> che ti ha comunicato lo Studio.</p>
          <div>
            <Label className="text-base font-semibold">ID di accesso</Label>
            <Input data-testid="activate-loginid" value={loginId} onChange={(e) => setLoginId(e.target.value)}
              required className="h-14 text-lg mt-1.5" placeholder="es. m.rossi" />
          </div>
          <div>
            <Label className="text-base font-semibold">Codice di attivazione</Label>
            <Input data-testid="activate-code" value={code} onChange={(e) => setCode(e.target.value)}
              required className="h-14 text-lg mt-1.5" placeholder="es. 7A8EC889" />
          </div>
          <div>
            <Label className="text-base font-semibold">Scegli una password</Label>
            <PasswordInput data-testid="activate-password" value={password} onChange={(e) => setPassword(e.target.value)}
              required className="h-14 text-lg mt-1.5" placeholder="almeno 8 caratteri" />
          </div>
          <div>
            <Label className="text-base font-semibold">Ripeti la password</Label>
            <PasswordInput data-testid="activate-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              required className="h-14 text-lg mt-1.5" placeholder="••••••••" />
          </div>
          {error && <div data-testid="activate-error" className="p-4 bg-red-50 border-2 border-red-200 text-red-800 rounded-lg text-base font-semibold">{error}</div>}
          <PrivacyConsent checked={privacyOk} onChange={setPrivacyOk} testid="activate-privacy" />
          <Button data-testid="activate-submit" type="submit" disabled={loading || !privacyOk} className="w-full h-14 text-lg font-bold bg-brand hover:bg-brand-dark">
            {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : "Attiva e accedi"}
          </Button>
          <button type="button" onClick={() => navigate("/login")} className="w-full text-center text-slate-600 font-semibold hover:underline flex items-center justify-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Torna all'accesso
          </button>
        </form>
      </div>
    </div>
  );
}
